/**
 * TakeoffEditor — full-screen PDF takeoff editor (Phase 2 of the takeoff tool).
 *
 * Opens one takeoff drawing (a single-page PDF extracted in Phase 1) for:
 *   * scale calibration — click two points, enter the real distance (ft + in);
 *     persists scale_points (visual fractions) + scale_points_per_foot
 *   * region drawing (only when opened with a proposalId) — category-first:
 *     pick a category in the right sidebar, then draw a polygon (SF), polyline
 *     (LF) or drop count markers; each region persists immediately with its
 *     computed value
 *
 * Architecture mirrors QcMarkupModal: PDF.js renders page 1 once to an
 * offscreen raster at a base scale; pan/zoom is a content transform on the
 * composite canvas; overlays redraw per frame in screen space. Geometry is
 * stored as page-relative VISUAL fractions [fx, fy] (see lib/pdfFrame.js and
 * database/101_takeoffs.sql) — never screen pixels or raw PDF user-space.
 *
 * Data goes through services/takeoffs.js only.
 */

import { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { loadPdfjs } from '../../lib/pdfjs.js'
import { alertDialog } from '../Dialog.jsx'
import { isFrame, visualSize, normalizeRotation } from '../../lib/pdfFrame.js'
import {
  TAKEOFF_CATEGORIES,
  updateDrawing, listRegions, createRegion, updateRegion, deleteRegion,
} from '../../services/takeoffs.js'
import {
  categoryColor, withAlpha, polygonCentroid, midpointAlong, pointInPolygon,
  distToSeg, computeRegionValue, scalePointsPerFoot, fmtValue, feetLabel,
} from './takeoffGeom.js'

const DARK = '#07272D'
const CAL_COLOR = '#1a6fa8'          // calibration points / scale chrome
const SELECT_COLOR = '#ec4899'       // selection accent (matches QC)
const TARGET_PX = 2800               // base raster long-edge target
const MAX_RASTER_DIM = 8192          // canvas safety caps (see QcMarkupModal)
const MAX_RASTER_AREA = 40 * 1024 * 1024
const RESHARPEN_MS = 220
const HANDLE = 7                     // vertex handle half-size (screen px)
const CLOSE_PX = 12                  // click-near-first-vertex close radius (screen px)
const SIDEBAR_W = 280
const BAR_CANDIDATES = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500]

const KIND_GROUPS = [
  { title: 'Areas', kind: 'area', unit: 'SF' },
  { title: 'Lengths', kind: 'line', unit: 'LF' },
  { title: 'Counts', kind: 'count', unit: 'qty' },
]
const KIND_HINT = {
  area: 'Click to add vertices. Click the first vertex or double-click to close.',
  line: 'Click to add points. Double-click to finish.',
  count: 'Click to drop a marker. One marker per click.',
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)) }
const round5 = v => Math.round(v * 100000) / 100000
const roundPt = f => [round5(f[0]), round5(f[1])]
const round2 = v => (v == null ? v : Math.round(v * 100) / 100)
const isTmp = id => String(id).startsWith('tmp_')
let tmpSeq = 0
const tmpId = () => 'tmp_' + Date.now() + '_' + (++tmpSeq)

/** Local scale model from a drawing row: { p1, p2, feet, sppf } or null. */
function scaleFromDrawing(d) {
  const sp = d?.scale_points
  const sppf = Number(d?.scale_points_per_foot)
  if (!sp || !Array.isArray(sp.p1) || !Array.isArray(sp.p2) || !(sppf > 0)) return null
  return { p1: [...sp.p1], p2: [...sp.p2], feet: Number(sp.feet) || 0, sppf }
}

export default function TakeoffEditor({ drawing, proposalId, opportunityName, onClose, onDrawingUpdated }) {
  const [vp, setVp] = useState({ x: 0, y: 0, z: 1 })
  const [tool, setTool] = useState('select')       // 'select' | 'scale' | 'draw'
  const [armed, setArmed] = useState(null)         // TAKEOFF_CATEGORIES entry when tool === 'draw'
  const [regions, setRegions] = useState(proposalId ? null : [])   // null = loading
  const [selectedId, setSelectedId] = useState(null)
  const [scale, setScale] = useState(() => scaleFromDrawing(drawing))
  const [bubble, setBubble] = useState(null)       // { p1, p2, feet:'', inches:'' } while entering the distance
  const [expandedCat, setExpandedCat] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [savingScale, setSavingScale] = useState(false)

  const containerRef = useRef(null)
  const compRef = useRef(null)
  const pageRef = useRef(null)
  const baseScaleRef = useRef(1)
  const rasterBaseRef = useRef(null)   // { canvas }
  const rasterHiRef = useRef(null)     // { canvas, z }
  const renderTaskRef = useRef(null)
  const rafRef = useRef(null)
  const resharpenRef = useRef(null)
  const dragRef = useRef(null)
  const draftRef = useRef(null)        // { pts: [[fx,fy],...] } in-progress polygon/polyline
  const hoverRef = useRef(null)        // [fx, fy] cursor position (draw/scale previews)
  const pendingP1Ref = useRef(null)    // first calibration click while placing a new scale
  const spaceRef = useRef(false)
  const frameRef = useRef(isFrame(drawing?.page_frame) ? drawing.page_frame : null)

  const st = useRef({})
  st.current = { vp, tool, armed, regions, selectedId, scale, bubble }

  /* ── Bootstrap: load PDF, render base raster, load regions ───────────────── */
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const pdfjs = await loadPdfjs()
        if (!pdfjs) throw new Error('PDF viewer failed to load. Check your connection and try again.')
        const resp = await fetch(drawing.pdf_url)
        if (!resp.ok) throw new Error('Could not fetch the drawing PDF (HTTP ' + resp.status + ').')
        const buf = await resp.arrayBuffer()
        if (cancelled) return
        const doc = await pdfjs.getDocument({ data: buf }).promise
        const page = await doc.getPage(1)
        if (cancelled) return
        pageRef.current = page
        // Frame: prefer the stored fingerprint (authoritative — parsed server-side
        // from this exact PDF); derive from pdf.js only if it is somehow missing.
        if (!frameRef.current) {
          const view = page.view
          frameRef.current = { w: view[2] - view[0], h: view[3] - view[1], rotation: normalizeRotation(page.rotate || 0), ox: view[0], oy: view[1] }
        }
        const vp1 = page.getViewport({ scale: 1 })
        const bs = clamp(TARGET_PX / Math.max(vp1.width, vp1.height), 0.5, 4)
        baseScaleRef.current = bs
        const base = await renderRaster(bs)
        if (cancelled || !base) return
        rasterBaseRef.current = base
        // Fit + center
        const cont = containerRef.current
        if (cont) {
          const cw = cont.clientWidth, ch = cont.clientHeight
          const fit = clamp(Math.min((cw - 40) / base.canvas.width, (ch - 40) / base.canvas.height), 0.05, 2)
          setVp({ x: Math.max(20, (cw - base.canvas.width * fit) / 2), y: Math.max(20, (ch - base.canvas.height * fit) / 2), z: fit })
        }
        setLoading(false)
        scheduleRender()
      } catch (e) {
        if (!cancelled) { setError(e.message || 'Could not open the drawing.'); setLoading(false) }
      }
    })()
    return () => { cancelled = true; if (renderTaskRef.current) { try { renderTaskRef.current.cancel() } catch { /* ignore */ } } }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Regions for this proposal, filtered to this drawing (service returns per-proposal).
  useEffect(() => {
    if (!proposalId) return
    let cancelled = false
    listRegions(proposalId)
      .then(rows => { if (!cancelled) setRegions((rows || []).filter(r => r.drawing_id === drawing.id)) })
      .catch(() => { if (!cancelled) setRegions([]) })
    return () => { cancelled = true }
  }, [proposalId, drawing.id])

  // Lock background scroll while open (html + body, same as QC).
  useEffect(() => {
    const b = document.body.style.overflow, h = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'; document.documentElement.style.overflow = 'hidden'
    return () => { document.body.style.overflow = b; document.documentElement.style.overflow = h }
  }, [])

  // Redraw after every commit (vp/regions/selection/scale/tool changes).
  useEffect(() => { scheduleRender() })

  // Repaint on window resize (canvas is sized from its container each frame).
  useEffect(() => {
    const onResize = () => scheduleRender()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  /* ── Raster helpers (QC pattern: base raster + zoom re-sharpen) ──────────── */
  async function renderRaster(requestScale) {
    const page = pageRef.current
    if (!page) return null
    const vp1 = page.getViewport({ scale: 1 })
    const longEdge = Math.max(vp1.width, vp1.height)
    const area = vp1.width * vp1.height
    const safeScale = Math.max(0.1, Math.min(requestScale, MAX_RASTER_DIM / longEdge, Math.sqrt(MAX_RASTER_AREA / area)))
    const viewport = page.getViewport({ scale: safeScale })
    const c = document.createElement('canvas')
    c.width = Math.ceil(viewport.width); c.height = Math.ceil(viewport.height)
    if (renderTaskRef.current) { try { renderTaskRef.current.cancel() } catch { /* ignore */ } }
    const task = page.render({ canvasContext: c.getContext('2d'), viewport })
    renderTaskRef.current = task
    try { await task.promise } catch (e) { if (e?.name === 'RenderingCancelledException') return null; throw e }
    return { canvas: c }
  }

  async function resharpen() {
    if (!pageRef.current) return
    const z = st.current.vp.z
    const hi = await renderRaster(baseScaleRef.current * z)
    if (!hi) return
    rasterHiRef.current = { canvas: hi.canvas, z }
    scheduleRender()
  }

  /* ── Coordinate chain ─────────────────────────────────────────────────────── */
  // screen px (canvas-relative) → content px → visual fraction, and back. The base
  // raster IS the rotated visual page (pdf.js viewports are rotation-normalized),
  // so fraction = content / raster size — exactly the stored convention.
  function baseDims() { const b = rasterBaseRef.current; return b ? { bw: b.canvas.width, bh: b.canvas.height } : null }
  function evtScreen(e) { const r = compRef.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top] }
  function screenToContent(sx, sy) { const v = st.current.vp; return [(sx - v.x) / v.z, (sy - v.y) / v.z] }
  function contentToScreen(cx, cy) { const v = st.current.vp; return [cx * v.z + v.x, cy * v.z + v.y] }
  function contentToFrac(cx, cy) { const d = baseDims(); return d ? [cx / d.bw, cy / d.bh] : [0, 0] }
  function fracToContent(f) { const d = baseDims(); return d ? [f[0] * d.bw, f[1] * d.bh] : [0, 0] }
  function fracToScreen(f) { const c = fracToContent(f); return contentToScreen(c[0], c[1]) }
  function evtFrac(e) { const [sx, sy] = evtScreen(e); const [cx, cy] = screenToContent(sx, sy); return contentToFrac(cx, cy) }
  /** Content px per PDF point of the visual page (for the scale bar). */
  function pxPerPoint() {
    const d = baseDims(), fr = frameRef.current
    if (!d || !fr) return 1
    return d.bw / visualSize(fr).vw
  }

  /* ── Composite render ─────────────────────────────────────────────────────── */
  function scheduleRender() { if (rafRef.current) return; rafRef.current = requestAnimationFrame(() => { rafRef.current = null; renderAll() }) }

  function renderAll() {
    const comp = compRef.current, cont = containerRef.current, base = rasterBaseRef.current
    if (!comp || !cont) return
    const dpr = window.devicePixelRatio || 1
    const cw = cont.clientWidth, ch = cont.clientHeight
    if (comp.width !== Math.floor(cw * dpr) || comp.height !== Math.floor(ch * dpr)) {
      comp.width = Math.floor(cw * dpr); comp.height = Math.floor(ch * dpr)
      comp.style.width = cw + 'px'; comp.style.height = ch + 'px'
    }
    const g = comp.getContext('2d')
    g.setTransform(dpr, 0, 0, dpr, 0, 0)
    g.clearRect(0, 0, cw, ch)
    if (!base) return
    const v = st.current.vp
    const baseW = base.canvas.width, baseH = base.canvas.height
    g.save(); g.translate(v.x, v.y); g.scale(v.z, v.z)
    const hi = rasterHiRef.current
    if (hi && Math.abs(hi.z - v.z) < 0.02) g.drawImage(hi.canvas, 0, 0, baseW, baseH)
    else g.drawImage(base.canvas, 0, 0, baseW, baseH)
    g.restore()
    // Overlays in screen space (constant-width strokes and crisp labels at any zoom)
    for (const r of st.current.regions || []) { if (r.id !== st.current.selectedId) drawRegion(g, r, false) }
    const sel = (st.current.regions || []).find(r => r.id === st.current.selectedId)
    if (sel) drawRegion(g, sel, true)
    drawDraft(g)
    drawCalibration(g)
    drawScaleBar(g, cw, ch)
  }

  function drawLabel(g, text, x, y, color) {
    g.save()
    g.font = '600 11px "DM Mono", monospace'
    g.textAlign = 'center'; g.textBaseline = 'middle'
    g.lineWidth = 3.5; g.strokeStyle = 'rgba(255,255,255,0.92)'; g.lineJoin = 'round'
    g.strokeText(text, x, y)
    g.fillStyle = color || '#1f2937'
    g.fillText(text, x, y)
    g.restore()
  }

  function drawRegion(g, r, selected) {
    const pts = Array.isArray(r.points) ? r.points : []
    if (!pts.length) return
    const col = categoryColor(r.category)
    const S = pts.map(p => fracToScreen(p))
    g.save()
    if (r.kind === 'area' && S.length >= 2) {
      g.beginPath(); g.moveTo(S[0][0], S[0][1])
      for (let i = 1; i < S.length; i++) g.lineTo(S[i][0], S[i][1])
      g.closePath()
      g.fillStyle = withAlpha(col, selected ? 0.3 : 0.2); g.fill()
      g.strokeStyle = selected ? SELECT_COLOR : col; g.lineWidth = selected ? 2.5 : 1.8; g.stroke()
      if (S.length >= 3) {
        const c = polygonCentroid(S)
        drawLabel(g, fmtValue('area', r.computed_value == null ? null : Number(r.computed_value)), c[0], c[1], col)
      }
    } else if (r.kind === 'line' && S.length >= 2) {
      g.beginPath(); g.moveTo(S[0][0], S[0][1])
      for (let i = 1; i < S.length; i++) g.lineTo(S[i][0], S[i][1])
      g.strokeStyle = selected ? SELECT_COLOR : col; g.lineWidth = 2.5; g.lineCap = 'round'; g.lineJoin = 'round'; g.stroke()
      ;[S[0], S[S.length - 1]].forEach(p => { g.beginPath(); g.arc(p[0], p[1], 3.5, 0, 2 * Math.PI); g.fillStyle = selected ? SELECT_COLOR : col; g.fill() })
      const m = midpointAlong(S)
      drawLabel(g, fmtValue('line', r.computed_value == null ? null : Number(r.computed_value)), m[0], m[1] - 10, col)
    } else if (r.kind === 'count') {
      const p = S[0]
      g.beginPath(); g.arc(p[0], p[1], 8, 0, 2 * Math.PI)
      g.fillStyle = col; g.fill()
      g.lineWidth = 2; g.strokeStyle = '#fff'; g.stroke()
      if (selected) { g.beginPath(); g.arc(p[0], p[1], 13, 0, 2 * Math.PI); g.lineWidth = 2.5; g.strokeStyle = SELECT_COLOR; g.stroke() }
    }
    // Vertex handles on the selected region (draggable)
    if (selected && r.kind !== 'count') {
      g.fillStyle = '#fff'; g.strokeStyle = SELECT_COLOR; g.lineWidth = 1.5
      for (const p of S) { g.fillRect(p[0] - HANDLE / 2, p[1] - HANDLE / 2, HANDLE, HANDLE); g.strokeRect(p[0] - HANDLE / 2, p[1] - HANDLE / 2, HANDLE, HANDLE) }
    }
    g.restore()
  }

  function drawDraft(g) {
    const d = draftRef.current
    if (!d || !st.current.armed) return
    const cat = st.current.armed
    const col = categoryColor(cat.key)
    const S = d.pts.map(p => fracToScreen(p))
    const hov = hoverRef.current ? fracToScreen(hoverRef.current) : null
    const all = hov ? S.concat([hov]) : S
    if (!all.length) return
    g.save()
    g.beginPath(); g.moveTo(all[0][0], all[0][1])
    for (let i = 1; i < all.length; i++) g.lineTo(all[i][0], all[i][1])
    if (cat.kind === 'area' && all.length >= 3) {
      g.closePath()
      g.fillStyle = withAlpha(col, 0.14); g.fill()
    }
    g.strokeStyle = col; g.lineWidth = 1.8; g.setLineDash([5, 3]); g.stroke(); g.setLineDash([])
    // Vertex dots; first vertex highlighted as the close target for polygons
    S.forEach((p, i) => {
      g.beginPath(); g.arc(p[0], p[1], i === 0 && cat.kind === 'area' ? 5 : 3.5, 0, 2 * Math.PI)
      g.fillStyle = i === 0 ? col : '#fff'; g.fill()
      g.strokeStyle = col; g.lineWidth = 1.3; g.stroke()
    })
    // Running measurement near the cursor
    if (hov && all.length >= 2 && scaleReady()) {
      const fracPts = d.pts.concat([hoverRef.current])
      const val = computeRegionValue(cat.kind === 'area' && fracPts.length >= 3 ? 'area' : 'line', fracPts, frameRef.current, st.current.scale.sppf)
      if (val != null) drawLabel(g, fmtValue(cat.kind === 'area' && fracPts.length >= 3 ? 'area' : 'line', val), hov[0] + 14, hov[1] - 12, col)
    }
    g.restore()
  }

  function calHandles() {
    // The draggable calibration points currently on screen: applied scale points,
    // or the pending first click while placing a new scale.
    const s = st.current.scale
    const out = []
    if (s) { out.push({ which: 'p1', frac: s.p1 }, { which: 'p2', frac: s.p2 }) }
    else if (pendingP1Ref.current) out.push({ which: 'pending', frac: pendingP1Ref.current })
    return out
  }

  function drawCalibration(g) {
    // Priority: an open distance bubble shows ITS pair (a recalibration pair may
    // differ from the applied one); a pending first click shows point + cursor
    // line; otherwise the applied calibration pair.
    const s = st.current.scale
    const b = st.current.bubble
    const pending = pendingP1Ref.current
    const pts = b ? [b.p1, b.p2] : (pending ? [pending] : (s ? [s.p1, s.p2] : []))
    if (!pts.length) return
    const S = pts.map(p => fracToScreen(p))
    g.save()
    // Connecting line: applied pair, or pending point → cursor
    let lineTo = null
    if (S.length === 2) lineTo = S[1]
    else if (st.current.tool === 'scale' && hoverRef.current) lineTo = fracToScreen(hoverRef.current)
    if (lineTo) {
      g.beginPath(); g.moveTo(S[0][0], S[0][1]); g.lineTo(lineTo[0], lineTo[1])
      g.strokeStyle = CAL_COLOR; g.lineWidth = 1.6; g.setLineDash([6, 4]); g.stroke(); g.setLineDash([])
    }
    for (const p of S) {
      g.beginPath(); g.arc(p[0], p[1], 7, 0, 2 * Math.PI)
      g.fillStyle = 'rgba(255,255,255,0.9)'; g.fill()
      g.lineWidth = 2; g.strokeStyle = CAL_COLOR; g.stroke()
      g.beginPath(); g.moveTo(p[0] - 3.5, p[1]); g.lineTo(p[0] + 3.5, p[1]); g.moveTo(p[0], p[1] - 3.5); g.lineTo(p[0], p[1] + 3.5)
      g.lineWidth = 1.4; g.stroke()
    }
    if (!b && !pending && s && S.length === 2) {
      drawLabel(g, feetLabel(s.feet), (S[0][0] + S[1][0]) / 2, (S[0][1] + S[1][1]) / 2 - 14, CAL_COLOR)
    }
    g.restore()
  }

  function drawScaleBar(g, cw, ch) {
    const s = st.current.scale
    if (!s || !(s.sppf > 0)) return
    const pxPerFt = s.sppf * pxPerPoint() * st.current.vp.z
    if (!(pxPerFt > 0)) return
    let barFt = BAR_CANDIDATES[0]
    for (const c of BAR_CANDIDATES) { if (c * pxPerFt <= 180) barFt = c; else break }
    const barPx = barFt * pxPerFt
    if (barPx < 24) return
    const bx = 16, by = ch - 20
    g.save()
    g.fillStyle = 'rgba(255,255,255,0.85)'
    g.fillRect(bx - 8, by - 16, barPx + 58, 28)
    g.strokeStyle = DARK; g.lineWidth = 2
    g.beginPath(); g.moveTo(bx, by); g.lineTo(bx + barPx, by); g.stroke()
    g.beginPath(); g.moveTo(bx, by - 5); g.lineTo(bx, by + 5); g.stroke()
    g.beginPath(); g.moveTo(bx + barPx, by - 5); g.lineTo(bx + barPx, by + 5); g.stroke()
    g.fillStyle = DARK; g.font = '600 10px "DM Mono", monospace'; g.textAlign = 'left'; g.textBaseline = 'middle'
    g.fillText(barFt + "'", bx + barPx + 8, by - 1)
    g.restore()
  }

  /* ── Hit testing (screen space) ───────────────────────────────────────────── */
  function hitCalHandle(sx, sy) {
    for (const h of calHandles()) {
      const p = fracToScreen(h.frac)
      if (Math.hypot(sx - p[0], sy - p[1]) <= 10) return h
    }
    return null
  }
  function hitVertexHandle(sx, sy) {
    const sel = (st.current.regions || []).find(r => r.id === st.current.selectedId)
    if (!sel || !Array.isArray(sel.points)) return null
    const pts = sel.kind === 'count' ? [sel.points[0]] : sel.points
    for (let i = 0; i < pts.length; i++) {
      const p = fracToScreen(pts[i])
      if (Math.abs(sx - p[0]) <= HANDLE + 3 && Math.abs(sy - p[1]) <= HANDLE + 3) return { id: sel.id, idx: i }
    }
    return null
  }
  function hitRegion(sx, sy) {
    const list = st.current.regions || []
    for (let i = list.length - 1; i >= 0; i--) {
      const r = list[i]
      const S = (r.points || []).map(p => fracToScreen(p))
      if (!S.length) continue
      if (r.kind === 'count') { if (Math.hypot(sx - S[0][0], sy - S[0][1]) <= 13) return r }
      else if (r.kind === 'line') { for (let j = 1; j < S.length; j++) if (distToSeg([sx, sy], S[j - 1], S[j]) <= 7) return r }
      else if (r.kind === 'area' && S.length >= 3) {
        if (pointInPolygon([sx, sy], S)) return r
        for (let j = 0; j < S.length; j++) if (distToSeg([sx, sy], S[j], S[(j + 1) % S.length]) <= 6) return r
      }
    }
    return null
  }

  /* ── Scale helpers ────────────────────────────────────────────────────────── */
  function scaleReady() { return !!(st.current.scale && st.current.scale.sppf > 0) }

  async function persistScale(p1, p2, feet, { alsoRecompute = true } = {}) {
    const fr = frameRef.current
    const sppf = scalePointsPerFoot(p1, p2, fr, feet)
    if (!(sppf > 0)) return null
    const sp = { p1: roundPt(p1), p2: roundPt(p2), feet }
    setSavingScale(true)
    try {
      const row = await updateDrawing(drawing.id, { scale_points: sp, scale_points_per_foot: sppf })
      const next = { p1: sp.p1, p2: sp.p2, feet, sppf }
      setScale(next)
      if (onDrawingUpdated && row) onDrawingUpdated(row)
      if (alsoRecompute) recomputeRegionValues(sppf)
      return next
    } catch (e) {
      await alertDialog({ title: 'Scale not saved', message: e.message || 'The scale could not be saved. Try again.' })
      return null
    } finally { setSavingScale(false) }
  }

  // Region values depend on the drawing scale: after a recalibration, recompute
  // and persist every loaded area/line region so stored values stay honest.
  function recomputeRegionValues(sppf) {
    const fr = frameRef.current
    const list = st.current.regions || []
    const updates = []
    const next = list.map(r => {
      if (r.kind === 'count' || isTmp(r.id)) return r
      const v = round2(computeRegionValue(r.kind, r.points, fr, sppf))
      if (v == null || v === r.computed_value) return r
      updates.push({ id: r.id, v })
      return { ...r, computed_value: v }
    })
    if (!updates.length) return
    setRegions(next)
    updates.forEach(u => updateRegion(u.id, { computed_value: u.v }).catch(() => {}))
  }

  async function applyBubble() {
    const b = st.current.bubble
    if (!b) return
    const feet = (parseFloat(b.feet) || 0) + (parseFloat(b.inches) || 0) / 12
    if (!(feet > 0)) return
    const ok = await persistScale(b.p1, b.p2, feet)
    if (ok) { setBubble(null); pendingP1Ref.current = null; setTool('select') }
  }

  function cancelBubble() {
    setBubble(null)
    pendingP1Ref.current = null
    // A bubble opened for a NEW pair (no applied scale yet) discards the pair;
    // one opened after dragging an applied point just closes (drag already persisted).
    scheduleRender()
  }

  /* ── Region CRUD ──────────────────────────────────────────────────────────── */
  function createRegionOptimistic(cat, pts) {
    const value = round2(computeRegionValue(cat.kind, pts, frameRef.current, st.current.scale?.sppf))
    const temp = { id: tmpId(), drawing_id: drawing.id, proposal_airtable_id: proposalId, category: cat.key, kind: cat.kind, points: pts, computed_value: value }
    setRegions(prev => (prev || []).concat(temp))
    createRegion({ drawingId: drawing.id, proposalId, category: cat.key, kind: cat.kind, points: pts, computedValue: value == null ? undefined : value })
      .then(row => setRegions(prev => (prev || []).map(r => r.id === temp.id ? row : r)))
      .catch(async e => {
        setRegions(prev => (prev || []).filter(r => r.id !== temp.id))
        await alertDialog({ title: 'Region not saved', message: e.message || 'The region could not be saved. Try again.' })
      })
  }

  function commitDraft() {
    const d = draftRef.current, cat = st.current.armed
    draftRef.current = null
    if (!d || !cat) { scheduleRender(); return }
    // Drop consecutive near-duplicate points (double-click adds 1 or 2 extra clicks at the same spot)
    const pts = []
    for (const p of d.pts) {
      const last = pts[pts.length - 1]
      if (last) {
        const a = fracToScreen(last), b = fracToScreen(p)
        if (Math.hypot(a[0] - b[0], a[1] - b[1]) < 3) continue
      }
      pts.push(roundPt(p))
    }
    const minPts = cat.kind === 'area' ? 3 : 2
    if (pts.length < minPts) { scheduleRender(); return }
    createRegionOptimistic(cat, pts)
    scheduleRender()
  }

  function cancelDraft() { draftRef.current = null; scheduleRender() }

  async function removeRegion(r) {
    if (isTmp(r.id)) return   // still saving; ignore
    const prev = st.current.regions || []
    setRegions(prev.filter(x => x.id !== r.id))
    if (st.current.selectedId === r.id) setSelectedId(null)
    try { await deleteRegion(r.id) }
    catch (e) {
      setRegions(prev)
      await alertDialog({ title: 'Delete failed', message: e.message || 'The region could not be deleted.' })
    }
  }

  /* ── Pointer interaction ──────────────────────────────────────────────────── */
  function beginDrag(d) {
    dragRef.current = d
    window.addEventListener('pointermove', onDragMove)
    window.addEventListener('pointerup', onDragUp)
  }
  function onDragMove(e) {
    const d = dragRef.current
    if (!d) return
    if (d.mode === 'pan') { setVp(v => ({ ...v, x: d.vp0.x + (e.clientX - d.sx), y: d.vp0.y + (e.clientY - d.sy) })); return }
    const frac = evtFrac(e)
    if (d.mode === 'cal') {
      d.moved = true
      if (d.which === 'pending') { pendingP1Ref.current = frac; scheduleRender() }
      else setScale(s => s ? { ...s, [d.which]: frac } : s)
    } else if (d.mode === 'vertex') {
      d.moved = true
      setRegions(prev => (prev || []).map(r => r.id === d.id ? { ...r, points: r.points.map((p, i) => i === d.idx ? frac : p) } : r))
    }
  }
  function onDragUp() {
    const d = dragRef.current
    dragRef.current = null
    window.removeEventListener('pointermove', onDragMove)
    window.removeEventListener('pointerup', onDragUp)
    if (!d) return
    if (d.mode === 'cal' && d.which !== 'pending' && d.moved) {
      // Dragging an applied calibration point: recompute with the stored feet,
      // persist, then reopen the distance bubble prefilled so it can be adjusted.
      const s = st.current.scale
      if (s && s.feet > 0) {
        persistScale(s.p1, s.p2, s.feet).then(next => {
          if (!next) return
          const totalIn = Math.round(next.feet * 12)
          setBubble({ p1: next.p1, p2: next.p2, feet: String(Math.floor(totalIn / 12)), inches: String(totalIn % 12) })
        })
      }
    } else if (d.mode === 'vertex' && d.moved) {
      const r = (st.current.regions || []).find(x => x.id === d.id)
      if (r && !isTmp(r.id)) {
        const pts = r.points.map(roundPt)
        const v = round2(computeRegionValue(r.kind, pts, frameRef.current, st.current.scale?.sppf))
        setRegions(prev => (prev || []).map(x => x.id === r.id ? { ...x, points: pts, computed_value: v ?? x.computed_value } : x))
        updateRegion(r.id, { points: pts, ...(v == null ? {} : { computed_value: v }) }).catch(() => {})
      }
    }
    scheduleRender()
  }

  function onPointerDown(e) {
    if (loading || error || !rasterBaseRef.current) return
    if (st.current.bubble) return   // distance bubble is open; it owns the interaction
    // Middle button or held space always pans
    if (e.button === 1 || spaceRef.current) { e.preventDefault(); beginDrag({ mode: 'pan', sx: e.clientX, sy: e.clientY, vp0: { ...st.current.vp } }); return }
    if (e.button !== 0) return
    const [sx, sy] = evtScreen(e)
    const frac = contentToFrac(...screenToContent(sx, sy))
    const t = st.current.tool

    // Calibration points are draggable in select and scale tools
    if (t === 'select' || t === 'scale') {
      const cal = hitCalHandle(sx, sy)
      if (cal) { beginDrag({ mode: 'cal', which: cal.which }); return }
    }

    if (t === 'scale') {
      if (!pendingP1Ref.current) { pendingP1Ref.current = frac; scheduleRender(); return }
      // Second click completes the pair and opens the distance bubble
      const p1 = pendingP1Ref.current
      pendingP1Ref.current = null
      const s = st.current.scale
      const prefill = s && s.feet > 0 ? Math.round(s.feet * 12) : 0
      setBubble({ p1, p2: frac, feet: prefill ? String(Math.floor(prefill / 12)) : '', inches: prefill ? String(prefill % 12) : '' })
      return
    }

    if (t === 'draw' && st.current.armed) {
      const cat = st.current.armed
      if (cat.kind === 'count') { createRegionOptimistic(cat, [roundPt(frac)]); return }
      const d = draftRef.current
      // Close the polygon by clicking near the first vertex
      if (cat.kind === 'area' && d && d.pts.length >= 3) {
        const first = fracToScreen(d.pts[0])
        if (Math.hypot(sx - first[0], sy - first[1]) <= CLOSE_PX) { commitDraft(); return }
      }
      if (!d) draftRef.current = { pts: [frac] }
      else d.pts.push(frac)
      scheduleRender()
      return
    }

    // Select tool
    const vh = hitVertexHandle(sx, sy)
    if (vh) { beginDrag({ mode: 'vertex', id: vh.id, idx: vh.idx }); return }
    const hit = hitRegion(sx, sy)
    if (hit) { setSelectedId(hit.id); return }
    setSelectedId(null)
    beginDrag({ mode: 'pan', sx: e.clientX, sy: e.clientY, vp0: { ...st.current.vp } })
  }

  function onDoubleClick() {
    if (st.current.tool === 'draw' && draftRef.current) commitDraft()
  }

  function onCanvasMove(e) {
    const t = st.current.tool
    if (t === 'scale' || (t === 'draw' && st.current.armed && st.current.armed.kind !== 'count')) {
      hoverRef.current = evtFrac(e)
      scheduleRender()
    }
  }

  /* ── Wheel zoom toward the cursor (non-passive) ───────────────────────────── */
  useEffect(() => {
    const cont = containerRef.current
    if (!cont) return
    function onWheel(e) {
      if (!rasterBaseRef.current) return
      e.preventDefault()
      const v = st.current.vp
      const ns = clamp(+(v.z * (e.deltaY < 0 ? 1.12 : 1 / 1.12)).toFixed(3), 0.03, 8)
      if (ns === v.z) return
      const r = compRef.current.getBoundingClientRect()
      const lx = e.clientX - r.left, ly = e.clientY - r.top
      const f = 1 - ns / v.z
      rasterHiRef.current = null
      setVp({ x: v.x + (lx - v.x) * f, y: v.y + (ly - v.y) * f, z: ns })
      if (resharpenRef.current) clearTimeout(resharpenRef.current)
      resharpenRef.current = setTimeout(resharpen, RESHARPEN_MS)
    }
    cont.addEventListener('wheel', onWheel, { passive: false })
    return () => cont.removeEventListener('wheel', onWheel)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function zoomBtn(dir) {
    const v = st.current.vp
    const ns = clamp(+(v.z * (dir > 0 ? 1.25 : 1 / 1.25)).toFixed(3), 0.03, 8)
    const cont = containerRef.current
    const cx = cont ? cont.clientWidth / 2 : 0, cy = cont ? cont.clientHeight / 2 : 0
    const f = 1 - ns / v.z
    rasterHiRef.current = null
    setVp({ x: v.x + (cx - v.x) * f, y: v.y + (cy - v.y) * f, z: ns })
    if (resharpenRef.current) clearTimeout(resharpenRef.current)
    resharpenRef.current = setTimeout(resharpen, RESHARPEN_MS)
  }

  // Pan+zoom so a region is centered and framed.
  function centerRegion(r) {
    const cont = containerRef.current
    if (!cont || !rasterBaseRef.current || !Array.isArray(r.points) || !r.points.length) return
    const C = r.points.map(p => fracToContent(p))
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const p of C) { minX = Math.min(minX, p[0]); minY = Math.min(minY, p[1]); maxX = Math.max(maxX, p[0]); maxY = Math.max(maxY, p[1]) }
    const b = { x: minX, y: minY, w: Math.max(maxX - minX, 20), h: Math.max(maxY - minY, 20) }
    const cw = cont.clientWidth, ch = cont.clientHeight, pad = 140
    const z = clamp(Math.min((cw - pad) / b.w, (ch - pad) / b.h), 0.05, 2.5)
    rasterHiRef.current = null
    setVp({ x: cw / 2 - (b.x + b.w / 2) * z, y: ch / 2 - (b.y + b.h / 2) * z, z })
    setSelectedId(r.id)
    if (resharpenRef.current) clearTimeout(resharpenRef.current)
    resharpenRef.current = setTimeout(resharpen, RESHARPEN_MS)
  }

  /* ── Keyboard ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    function onKey(e) {
      const ae = document.activeElement
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT' || ae.isContentEditable)) {
        if (e.key === 'Escape' && st.current.bubble) { e.preventDefault(); cancelBubble() }
        return
      }
      if (e.key === ' ') { spaceRef.current = true; return }
      if (e.key === 'Escape') {
        e.preventDefault()
        if (st.current.bubble) { cancelBubble(); return }
        if (pendingP1Ref.current) { pendingP1Ref.current = null; scheduleRender(); return }
        if (draftRef.current) { cancelDraft(); return }
        if (st.current.tool !== 'select') { setTool('select'); setArmed(null); return }
        if (st.current.selectedId) { setSelectedId(null); return }
        return
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && st.current.selectedId) {
        const r = (st.current.regions || []).find(x => x.id === st.current.selectedId)
        if (r) { e.preventDefault(); removeRegion(r) }
        return
      }
      if (e.key === 'v' || e.key === 'V') { setTool('select'); setArmed(null); cancelDraft(); return }
      if (e.key === 's' || e.key === 'S') { startScaleTool(); return }
    }
    function onKeyUp(e) { if (e.key === ' ') spaceRef.current = false }
    window.addEventListener('keydown', onKey)
    window.addEventListener('keyup', onKeyUp)
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('keyup', onKeyUp) }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function startScaleTool() {
    setArmed(null); cancelDraft(); setSelectedId(null)
    pendingP1Ref.current = null
    setTool('scale')
  }

  async function armCategory(cat) {
    setExpandedCat(k => (k === cat.key ? k : cat.key))
    if (cat.kind !== 'count' && !scaleReady()) {
      await alertDialog({
        title: 'Set the scale first',
        message: 'This drawing has no scale yet. Use Set scale (press s) to calibrate a known distance, then draw ' + (cat.kind === 'area' ? 'areas' : 'lengths') + '.',
      })
      return
    }
    draftRef.current = null
    setSelectedId(null)
    setArmed(cat)
    setTool('draw')
  }

  /* ── Derived UI data ──────────────────────────────────────────────────────── */
  const catStats = useMemo(() => {
    const m = {}
    for (const r of regions || []) {
      const s = m[r.category] || (m[r.category] = { count: 0, total: 0, items: [] })
      s.count++
      if (r.computed_value != null) s.total += Number(r.computed_value) || 0
      s.items.push(r)
    }
    return m
  }, [regions])

  // Bubble anchor position (screen space, clamped to the container)
  const bubblePos = useMemo(() => {
    if (!bubble || !rasterBaseRef.current || !containerRef.current) return null
    const a = fracToScreen(bubble.p1), b = fracToScreen(bubble.p2)
    const cont = containerRef.current
    const bw = 252, bh = 118
    const x = clamp((a[0] + b[0]) / 2 - bw / 2, 8, Math.max(8, cont.clientWidth - bw - 8))
    const y = clamp((a[1] + b[1]) / 2 - bh - 16, 8, Math.max(8, cont.clientHeight - bh - 8))
    return { left: x, top: y, width: bw }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bubble, vp])

  const cursor = tool === 'select' ? 'default' : 'crosshair'

  return createPortal(
    <div data-modal-id="TakeoffEditor" style={{ position: 'fixed', inset: 0, zIndex: 760, background: 'var(--aj-bg-page)', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: DARK, color: '#fff', flexShrink: 0 }}>
        <span style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 700, fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Takeoff · {drawing.name}{opportunityName ? ' · ' + opportunityName : ''}
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={() => { setTool('select'); setArmed(null); cancelDraft() }} style={topBtn(tool === 'select')} title="Select and pan (v)">Select</button>
        <button onClick={startScaleTool} style={topBtn(tool === 'scale')} title="Calibrate the drawing scale (s)">Set scale</button>
        <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.25)' }} />
        <button onClick={() => zoomBtn(-1)} style={topBtn(false)}>−</button>
        <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, minWidth: 40, textAlign: 'center' }}>{Math.round(vp.z * 100)}%</span>
        <button onClick={() => zoomBtn(1)} style={topBtn(false)}>+</button>
        <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.25)' }} />
        {savingScale && <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: '#9fb4ba' }}>Saving…</span>}
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', lineHeight: 1 }} title="Close">×</button>
      </div>

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Canvas */}
        <div ref={containerRef} style={{ flex: 1, position: 'relative', overflow: 'hidden', background: 'var(--aj-bg-muted)', cursor }}>
          <canvas
            ref={compRef}
            onPointerDown={onPointerDown}
            onPointerMove={onCanvasMove}
            onDoubleClick={onDoubleClick}
            style={{ display: 'block', position: 'absolute', left: 0, top: 0, touchAction: 'none' }}
          />

          {/* Armed-category chip */}
          {tool === 'draw' && armed && (
            <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, padding: '5px 12px', boxShadow: '0 3px 10px rgba(0,0,0,0.14)', maxWidth: '80%' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: categoryColor(armed.key), flexShrink: 0 }} />
              <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--aj-text)', whiteSpace: 'nowrap' }}>{armed.label}</span>
              <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{KIND_HINT[armed.kind]}</span>
              <button onClick={() => { setTool('select'); setArmed(null); cancelDraft() }} style={{ background: 'none', border: 'none', color: 'var(--aj-text-muted)', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 }} title="Stop drawing (Esc)">×</button>
            </div>
          )}

          {/* Scale-placement hint */}
          {tool === 'scale' && !bubble && (
            <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, padding: '5px 14px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text)', boxShadow: '0 3px 10px rgba(0,0,0,0.14)', whiteSpace: 'nowrap' }}>
              {pendingP1Ref.current ? 'Click the second point of a known distance' : 'Click the first point of a known distance (a dimension line works well)'}
            </div>
          )}

          {/* Distance bubble */}
          {bubble && bubblePos && (
            <div style={{ position: 'absolute', left: bubblePos.left, top: bubblePos.top, width: bubblePos.width, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.22)', padding: 12 }}>
              <div style={{ fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 8 }}>Real distance</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="number" min="0" autoFocus value={bubble.feet} placeholder="0"
                  onChange={e => setBubble(b => b ? { ...b, feet: e.target.value } : b)}
                  onKeyDown={e => { if (e.key === 'Enter') applyBubble() }}
                  style={bubbleInput} />
                <span style={bubbleUnit}>ft</span>
                <input type="number" min="0" max="11" value={bubble.inches} placeholder="0"
                  onChange={e => setBubble(b => b ? { ...b, inches: e.target.value } : b)}
                  onKeyDown={e => { if (e.key === 'Enter') applyBubble() }}
                  style={bubbleInput} />
                <span style={bubbleUnit}>in</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                <button onClick={cancelBubble} style={ghostBtn}>Cancel</button>
                <button onClick={applyBubble} disabled={savingScale || !(((parseFloat(bubble.feet) || 0) + (parseFloat(bubble.inches) || 0) / 12) > 0)}
                  style={{ ...primaryBtn, opacity: savingScale || !(((parseFloat(bubble.feet) || 0) + (parseFloat(bubble.inches) || 0) / 12) > 0) ? 0.5 : 1 }}>
                  {savingScale ? 'Saving…' : 'Apply'}
                </button>
              </div>
            </div>
          )}

          {loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: 'var(--aj-bg-muted)' }}>
              <div style={{ width: 26, height: 26, border: '3px solid var(--aj-border)', borderTopColor: 'var(--aj-accent-blue)', borderRadius: '50%', animation: 'tkspin 0.8s linear infinite' }} />
              <span style={{ color: 'var(--aj-text-mid)', fontFamily: '"DM Sans", sans-serif', fontSize: 13 }}>Loading drawing…</span>
              <style>{'@keyframes tkspin{to{transform:rotate(360deg)}}'}</style>
            </div>
          )}
          {error && !loading && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 24, textAlign: 'center', background: 'var(--aj-bg-muted)' }}>
              <div style={{ color: 'var(--aj-text)', fontFamily: '"Montserrat", sans-serif', fontSize: 14, fontWeight: 400 }}>Could not open the drawing</div>
              <div style={{ color: 'var(--aj-text-mid)', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, maxWidth: 340 }}>{error}</div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div style={{ width: SIDEBAR_W, flexShrink: 0, background: 'var(--aj-bg-elevated)', borderLeft: '0.5px solid var(--aj-border-mid)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Scale status */}
          <div style={{ padding: '12px 14px', borderBottom: '0.5px solid var(--aj-border)' }}>
            <div style={sideHeading}>Scale</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7 }}>
              {scale ? (
                <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: '#1a7f4e', background: 'rgba(26,127,78,0.12)', borderRadius: 4, padding: '3px 8px' }}>
                  Scale set · {feetLabel(scale.feet)} reference
                </span>
              ) : (
                <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'var(--aj-text-muted)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '3px 8px' }}>
                  Not scaled
                </span>
              )}
              <div style={{ flex: 1 }} />
              <button onClick={startScaleTool} style={{ ...ghostBtn, padding: '3px 9px' }}>{scale ? 'Recalibrate' : 'Set scale'}</button>
            </div>
            {scale && <div style={{ marginTop: 6, fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)' }}>Drag a calibration point to adjust it.</div>}
          </div>

          {/* Categories / regions */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {!proposalId ? (
              <div style={{ padding: '14px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', lineHeight: 1.55 }}>
                Open this drawing from a proposal to draw takeoff regions. Scale calibration set here carries over.
              </div>
            ) : regions === null ? (
              <div style={{ padding: '14px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', fontStyle: 'italic' }}>Loading regions…</div>
            ) : KIND_GROUPS.map(gr => {
              const cats = TAKEOFF_CATEGORIES.filter(c => c.kind === gr.kind)
              const withR = cats.filter(c => (catStats[c.key]?.count || 0) > 0)
              const without = cats.filter(c => !(catStats[c.key]?.count > 0))
              const ordered = withR.concat(without)
              return (
                <div key={gr.kind}>
                  <div style={{ ...sideHeading, padding: '11px 14px 5px', display: 'flex', alignItems: 'baseline' }}>
                    <span>{gr.title}</span>
                    <span style={{ marginLeft: 6, fontFamily: '"DM Mono", monospace', fontSize: 9, color: 'var(--aj-text-muted)', letterSpacing: 0, textTransform: 'none' }}>{gr.unit}</span>
                  </div>
                  {ordered.map(cat => {
                    const s = catStats[cat.key]
                    const isArmed = armed?.key === cat.key && tool === 'draw'
                    const expanded = expandedCat === cat.key && (s?.count || 0) > 0
                    return (
                      <div key={cat.key}>
                        <div
                          onClick={() => armCategory(cat)}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', cursor: 'pointer', background: isArmed ? withAlpha(categoryColor(cat.key), 0.1) : 'transparent', borderLeft: isArmed ? '2px solid ' + categoryColor(cat.key) : '2px solid transparent' }}
                          title={'Draw ' + cat.label + ' regions'}
                        >
                          <span style={{ width: 10, height: 10, borderRadius: 3, background: categoryColor(cat.key), flexShrink: 0, opacity: s?.count ? 1 : 0.45 }} />
                          <span style={{ flex: 1, fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: s?.count ? 'var(--aj-text)' : 'var(--aj-text-mid)', fontWeight: s?.count ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.label}</span>
                          {s?.count > 0 && (
                            <button onClick={e => { e.stopPropagation(); setExpandedCat(k => k === cat.key ? null : cat.key) }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 9, padding: '0 2px' }}
                              title={expanded ? 'Collapse regions' : 'Show regions'}>
                              {expanded ? '▾' : '▸'}
                            </button>
                          )}
                          {s?.count > 0 && <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 9.5, color: 'var(--aj-text-muted)' }}>{s.count}</span>}
                          <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: s?.count ? 'var(--aj-text)' : 'var(--aj-text-muted)', minWidth: 52, textAlign: 'right' }}>
                            {s?.count ? fmtValue(cat.kind, s.total) : '—'}
                          </span>
                        </div>
                        {expanded && s.items.map((r, i) => (
                          <div key={r.id}
                            onClick={() => centerRegion(r)}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 14px 4px 32px', cursor: 'pointer', background: selectedId === r.id ? withAlpha(SELECT_COLOR, 0.08) : 'transparent' }}
                            title="Center on canvas">
                            <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 10, color: 'var(--aj-text-muted)', width: 18 }}>{i + 1}</span>
                            <span style={{ flex: 1, fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'var(--aj-text-mid)' }}>
                              {isTmp(r.id) ? 'Saving…' : fmtValue(r.kind, r.computed_value == null ? null : Number(r.computed_value))}
                            </span>
                            <button onClick={e => { e.stopPropagation(); removeRegion(r) }} disabled={isTmp(r.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 11, padding: '0 2px', lineHeight: 1 }}
                              title="Delete region">×</button>
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
              )
            })}
            {proposalId && regions !== null && (
              <div style={{ padding: '12px 14px 18px', fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', lineHeight: 1.5 }}>
                Click a category to start drawing. Click a region on the canvas to select it; drag its handles to adjust, press Delete to remove.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

/* ── Styles ─────────────────────────────────────────────────────────────────── */
function topBtn(active) {
  return {
    background: active ? 'rgba(255,255,255,0.18)' : 'transparent',
    border: '0.5px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 4,
    padding: '3px 10px', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontWeight: 600, lineHeight: 1.5, whiteSpace: 'nowrap',
  }
}
const sideHeading = { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' }
const bubbleInput = { width: 56, background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 7px', color: 'var(--aj-text)', fontFamily: '"DM Mono", monospace', fontSize: 12, outline: 'none' }
const bubbleUnit = { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-mid)' }
const ghostBtn = { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)', borderRadius: 5, padding: '4px 11px', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontWeight: 600, lineHeight: 1.5 }
const primaryBtn = { background: DARK, border: '0.5px solid ' + DARK, color: '#fff', borderRadius: 5, padding: '4px 12px', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, fontWeight: 600, lineHeight: 1.5 }
